import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ASSETS } from "@/lib/constants";
import { MAJORITY_BLACK_PLACES, BLACK_DISTRICT_CITIES, HISTORIC_BLACK_DISTRICTS, isCDP } from "@/lib/nationData";
import { CommunityMap } from "@/components/CommunityMap";
import { motion } from "framer-motion";
import { MapPin, Building2, Landmark, Search, ArrowRight, Users } from "lucide-react";

const FADE = { initial: { opacity: 0, y: 18 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.45 } };
const HERO_TEXT_MOTION = { initial: { opacity: 0, y: 22 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.7 } };
const HERO_IMG_MOTION = { initial: { opacity: 0, scale: 0.97 }, animate: { opacity: 1, scale: 1 }, transition: { duration: 0.8, delay: 0.15 } };

function StatBlock({ value, label, accent }) {
  return (
    <div className={`p-8 ${accent ? "bg-[#141414]" : "bg-[#0A0A0A]"}`}>
      <div className="font-serif-display text-5xl sm:text-6xl font-semibold text-[#D4AF37]">{value}</div>
      <div className="text-xs uppercase tracking-[0.15em] mt-2 text-[#A8A29E] leading-relaxed">{label}</div>
    </div>
  );
}

export default function TheNation() {
  const [query, setQuery] = useState("");

  const stats = useMemo(() => {
    const allPlaces = MAJORITY_BLACK_PLACES.flatMap((s) => s.places);
    const cdps = allPlaces.filter(isCDP).length;
    const districts = BLACK_DISTRICT_CITIES.reduce((n, c) => n + c.districts.length, 0);
    const historic = HISTORIC_BLACK_DISTRICTS.reduce((n, c) => n + c.communities.length, 0);
    return {
      states: MAJORITY_BLACK_PLACES.length,
      places: allPlaces.length,
      cdps,
      cities: BLACK_DISTRICT_CITIES.length,
      districts,
      historic,
    };
  }, []);

  const q = query.trim().toLowerCase();
  const filteredPlaces = useMemo(
    () => MAJORITY_BLACK_PLACES.filter((s) =>
      !q || s.state.toLowerCase().includes(q) || s.places.some((p) => p.toLowerCase().includes(q))
    ), [q]
  );
  const filteredCities = useMemo(
    () => BLACK_DISTRICT_CITIES.filter((c) =>
      !q || c.state.toLowerCase().includes(q) || c.city.toLowerCase().includes(q) || c.districts.some((d) => d.toLowerCase().includes(q))
    ), [q]
  );
  const filteredHistoric = useMemo(
    () => HISTORIC_BLACK_DISTRICTS.filter((c) =>
      !q || c.state.toLowerCase().includes(q) || c.communities.some((d) => d.toLowerCase().includes(q))
    ), [q]
  );

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />
      <div className="tribal-stripe h-1.5 w-full" />

      {/* Hero */}
      <section className="relative">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 py-16 lg:py-20 grid lg:grid-cols-2 gap-14 items-center">
          <motion.div {...HERO_TEXT_MOTION}>
            <div className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-semibold mb-5">One People · Fifty States</div>
            <h1 className="font-serif-display text-5xl sm:text-6xl font-semibold tracking-tight leading-[0.97] text-[#F5F3EF]">
              The Nation<br />Already Exists
            </h1>
            <p className="mt-7 text-lg text-[#A8A29E] leading-relaxed max-w-xl">
              A Black American nation-state is already here — not in one territory, but woven through
              <span className="text-[#F5F3EF] font-medium"> thousands of communities across the United States</span>.
              From majority-Black cities and towns to historic districts and cultural strongholds, our people already
              govern neighborhoods, build institutions, and carry a shared heritage.
            </p>
            <p className="mt-5 text-lg text-[#EDE9E1] leading-relaxed max-w-xl">
              The Black American Government (BAG) serves as the <span className="text-[#D4AF37] font-medium">unifying structure</span> —
              connecting these dispersed communities into one coordinated, self-determining people.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link to="/register" data-testid="nation-register-btn">
                <Button className="rounded-none bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#C8102E] hover:text-white px-8 py-6">Unite With Us <ArrowRight className="ml-2 w-4 h-4" /></Button>
              </Link>
              <Link to="/census" data-testid="nation-census-btn">
                <Button variant="outline" className="rounded-none bg-transparent border-[#D4AF37]/50 text-[#F5F3EF] hover:bg-[#D4AF37] hover:text-[#0A0A0A] px-8 py-6">Be Counted</Button>
              </Link>
            </div>
          </motion.div>

          <motion.div {...HERO_IMG_MOTION} className="relative">
            <div className="border border-[#D4AF37]/30 p-2">
              <img src={ASSETS.nationLeaders} alt="Leaders of the Indigenous Black American Nation in ceremonial regalia" className="w-full object-cover shadow-2xl shadow-black/60" data-testid="nation-hero-image" />
            </div>
            <div className="absolute -bottom-4 left-6 right-6 bg-black/85 border border-[#D4AF37]/40 px-5 py-3 backdrop-blur-sm">
              <p className="text-xs text-[#EDE9E1] tracking-wide text-center italic">"For Our People. By Our People. Through Our Oneness."</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats strip */}
      <section className="border-y border-[#D4AF37]/20 bg-black mt-6">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 py-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#D4AF37]/15 border border-[#D4AF37]/15">
            <StatBlock value={`${stats.states}+`} label="States & territories with majority-Black places" accent />
            <StatBlock value={`${stats.places}`} label="Majority-Black municipalities & CDPs (pop. 5,000+)" />
            <StatBlock value={`${stats.districts}+`} label="Majority-Black districts within larger cities" accent />
            <StatBlock value={`${stats.historic}+`} label="Historic Black cultural communities" />
          </div>
        </div>
      </section>

      {/* Methodology */}
      <section className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 py-14">
        <div className="bg-[#141414] border border-[#D4AF37]/30 p-6 sm:p-8 flex flex-col sm:flex-row gap-5">
          <Users className="w-8 h-8 text-[#D4AF37] shrink-0" strokeWidth={1.5} />
          <div className="text-sm text-[#A8A29E] leading-relaxed">
            <span className="text-[#F5F3EF] font-medium">About this record.</span> The lists below use the 2020 U.S. Census
            (PL 94-171) redistricting data, framing places where <span className="text-[#EDE9E1]">non-Hispanic Black / African American alone</span> exceeds
            50% of the population (5,000+ residents). Census "place" data includes cities, towns, villages, boroughs, and
            <span className="text-[#EDE9E1]"> Census Designated Places (CDPs)</span> — unincorporated communities, marked with a
            <span className="inline-block mx-1 px-1.5 py-0.5 border border-[#D4AF37]/50 text-[#D4AF37] text-[10px] tracking-wider align-middle">CDP</span>
            badge below. For neighborhood and district-level work, Census tract data is the more precise source.
          </div>
        </div>

        {/* Search */}
        <div className="relative max-w-md mt-10">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#A8A29E]" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by state, city, or community…"
            className="rounded-none pl-9 bg-transparent border-[#F5F3EF]/20 text-[#F5F3EF]" data-testid="nation-search-input" />
        </div>
      </section>

      {/* Community map */}
      <section className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 pb-4">
        <CommunityMap subheading="Majority-Black communities, neighborhoods, and historic districts dispersed across the United States — the living footprint of the Nation." />
      </section>

      {/* Table 1 — Majority-Black places */}
      <section className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 pb-8">
        <div className="flex items-center gap-3 mb-2">
          <MapPin className="w-5 h-5 text-[#D4AF37]" />
          <div className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-semibold">Table I</div>
        </div>
        <h2 className="font-serif-display text-4xl sm:text-5xl text-[#F5F3EF] tracking-tight mb-3">Majority-Black Municipalities & CDPs</h2>
        <p className="text-[#A8A29E] max-w-3xl mb-10">Places, population 5,000+, where Black Americans are the majority of residents (2020 Census).</p>

        {filteredPlaces.length === 0 ? (
          <p className="text-[#A8A29E] py-10">No places match "{query}".</p>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {filteredPlaces.map((s) => (
              <motion.div key={s.state} {...FADE} className="bg-[#141414] border border-[#D4AF37]/30 p-6" data-testid={`nation-state-${s.state.replace(/[^a-zA-Z]/g, "")}`}>
                <div className="flex items-baseline justify-between mb-4 border-b border-[#F5F3EF]/10 pb-3">
                  <h3 className="font-serif-display text-2xl text-[#F5F3EF]">{s.state}</h3>
                  <span className="text-xs text-[#A8A29E] font-mono-doc">{s.places.length} places</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {s.places.map((p) => {
                    const cdp = isCDP(p);
                    const label = cdp ? p.replace(/\s*CDP$/, "") : p;
                    return (
                      <span key={p} className={`inline-flex items-center gap-1.5 text-sm px-3 py-1.5 border ${cdp ? "border-[#D4AF37]/30 text-[#D6D2CA]" : "border-[#5DBB7A]/40 text-[#EDE9E1] bg-[#5DBB7A]/5"}`}>
                        {label}
                        {cdp && <span className="text-[9px] tracking-wider text-[#D4AF37] border border-[#D4AF37]/50 px-1">CDP</span>}
                      </span>
                    );
                  })}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </section>

      {/* Table 2 — District cities */}
      <section className="border-y border-[#D4AF37]/20 bg-black mt-10">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 py-16">
          <div className="flex items-center gap-3 mb-2">
            <Building2 className="w-5 h-5 text-[#D4AF37]" />
            <div className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-semibold">Table II</div>
          </div>
          <h2 className="font-serif-display text-4xl sm:text-5xl text-[#F5F3EF] tracking-tight mb-3">Majority-Black Districts Within Larger Cities</h2>
          <p className="text-[#A8A29E] max-w-3xl mb-10">In cities that are not majority-Black overall, our people anchor entire districts and neighborhoods.</p>

          {filteredCities.length === 0 ? (
            <p className="text-[#A8A29E] py-10">No cities match "{query}".</p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredCities.map((c) => (
                <motion.div key={`${c.state}-${c.city}`} {...FADE} className="bg-[#141414] border border-[#D4AF37]/25 p-6">
                  <div className="text-xs uppercase tracking-wider text-[#A8A29E] mb-1">{c.state}</div>
                  <h3 className="font-serif-display text-2xl text-[#F5F3EF] mb-3">{c.city}</h3>
                  <div className="flex flex-wrap gap-2">
                    {c.districts.map((d) => (
                      <span key={d} className="text-xs px-2.5 py-1 border border-[#F5F3EF]/15 text-[#D6D2CA]">{d}</span>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Bonus — Historic districts */}
      <section className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 py-16">
        <div className="flex items-center gap-3 mb-2">
          <Landmark className="w-5 h-5 text-[#D4AF37]" />
          <div className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-semibold">Cultural Strongholds</div>
        </div>
        <h2 className="font-serif-display text-4xl sm:text-5xl text-[#F5F3EF] tracking-tight mb-3">Historic Black American Districts & Communities</h2>
        <p className="text-[#A8A29E] max-w-3xl mb-10">The cultural heart of the Nation — districts that have carried our history, business, art, and resistance for generations.</p>

        {filteredHistoric.length === 0 ? (
          <p className="text-[#A8A29E] py-10">No communities match "{query}".</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredHistoric.map((c) => (
              <motion.div key={c.state} {...FADE} className="bg-[#141414] border border-[#D4AF37]/25 p-6">
                <h3 className="font-serif-display text-xl text-[#D4AF37] mb-3">{c.state}</h3>
                <ul className="space-y-1.5">
                  {c.communities.map((d) => (
                    <li key={d} className="text-sm text-[#D6D2CA] flex gap-2"><span className="text-[#D4AF37]">▸</span> {d}</li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        )}
      </section>

      {/* Call to local organizing */}
      <section className="border-y border-[#D4AF37]/20 bg-black">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 py-16 lg:py-20">
          <div className="grid lg:grid-cols-3 gap-12">
            <div>
              <Landmark className="w-8 h-8 text-[#D4AF37] mb-4" strokeWidth={1.5} />
              <div className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-semibold mb-4">A Call to Our Communities</div>
              <h2 className="font-serif-display text-3xl sm:text-4xl text-[#F5F3EF] tracking-tight leading-tight">Establish Your Local Tribal Government</h2>
            </div>
            <div className="lg:col-span-2 space-y-5 text-[#C9C5BD] leading-relaxed">
              <p>
                Every community named on these rolls is a seat of the Nation. We call on members from each of these
                <span className="text-[#F5F3EF] font-medium"> majority-Black municipalities, CDPs, districts, and historic communities</span> to
                join the Black American Government and <span className="text-[#D4AF37] font-medium">establish local tribal governments</span> where they live.
              </p>
              <p>
                Local tribal governments are the building blocks of the Nation — organizing neighbors, electing local
                councils, coordinating committees and programs, and representing their community within the unified
                national structure. What already exists in population becomes, through organization, a governing people.
              </p>
              <div className="grid sm:grid-cols-3 gap-px bg-[#D4AF37]/15 border border-[#D4AF37]/15 mt-2">
                {[
                  { n: "01", t: "Join", d: "Members register and identify their home community on the rolls." },
                  { n: "02", t: "Convene", d: "Neighbors gather to form a local council and elect representatives." },
                  { n: "03", t: "Govern", d: "The local tribal government runs committees and unites with the Nation." },
                ].map((step) => (
                  <div key={step.n} className="bg-black p-6">
                    <div className="font-mono-doc text-[#D4AF37] text-sm mb-2">{step.n}</div>
                    <h3 className="font-serif-display text-2xl text-[#F5F3EF] mb-1">{step.t}</h3>
                    <p className="text-sm text-[#A8A29E] leading-relaxed">{step.d}</p>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-4 pt-2">
                <Link to="/register" data-testid="nation-local-gov-register-btn">
                  <Button className="rounded-none bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#C8102E] hover:text-white px-8 py-6">Join & Organize My Community <ArrowRight className="ml-2 w-4 h-4" /></Button>
                </Link>
                <Link to="/committees" data-testid="nation-local-gov-committees-btn">
                  <Button variant="outline" className="rounded-none bg-transparent border-[#D4AF37]/50 text-[#F5F3EF] hover:bg-[#D4AF37] hover:text-[#0A0A0A] px-8 py-6">Start a Committee</Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 pb-20">
        <div className="bg-black border border-[#D4AF37]/30 p-10 sm:p-16 text-center">
          <h2 className="font-serif-display text-3xl sm:text-5xl text-[#F5F3EF] tracking-tight max-w-3xl mx-auto leading-tight">
            We are not scattered. We are <span className="text-[#D4AF37]">one Nation</span>, already here.
          </h2>
          <p className="mt-6 text-[#A8A29E] max-w-xl mx-auto">The BAG unifies these communities into a single, self-determining people. Add your community to the Nation.</p>
          <div className="flex flex-wrap justify-center gap-4 mt-9">
            <Link to="/register"><Button className="rounded-none bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#C8102E] hover:text-white px-8 py-6">Become a Member <ArrowRight className="ml-2 w-4 h-4" /></Button></Link>
            <Link to="/census"><Button variant="outline" className="rounded-none bg-transparent border-[#D4AF37]/50 text-[#F5F3EF] hover:bg-[#D4AF37] hover:text-[#0A0A0A] px-8 py-6">Record on the Census</Button></Link>
          </div>
        </div>
        <p className="text-xs text-[#A8A29E]/70 leading-relaxed mt-8 max-w-4xl mx-auto text-center">
          Community lists are compiled from 2020 U.S. Census (PL 94-171) place data and publicly documented historic
          districts, presented for educational and organizing purposes. District/neighborhood entries are indicative;
          precise boundaries require Census tract and block-group analysis.
        </p>
      </section>

      <Footer />
    </div>
  );
}
